import { Modal } from 'react-bootstrap';
import type { VoidFunctionComponent } from 'react';

import Locator from '../../../../shared/Locator';
import { ButtonGroup, icons } from '../../generic';
import Button from '../../generic/Button';
import type { ManagePackageLabelsState } from '../../projects/dependenciesV2/hooks/useManagePackageLabelsModal';

import { ManagePackageLabelsBulkContent } from './ManagePackageLabelsBulkContent';
import { ManagePackageLabelsContent } from './ManagePackageLabelsContent';
import { Tab } from './types';
import { useBulkPackageLabelState } from './useBulkPackageLabelState';
import { usePackageLabelState } from './usePackageLabelState';

import styles from './ModalManagePackageLabels.module.less';

/**
 * Message shown on other, disabled tabs when the current tab has pending changes.
 */
export const PENDING_CHANGES_TOOLTIP = 'Please save or cancel any pending label changes.';

type ModalProps = {
  context: ManagePackageLabelsState;
  // The locator of the user revision that imports the dependency.
  // If provided, then an issue scan will be performed after any changes are made.
  parentRevisionLocator?: string;

  // Modal state management
  show: boolean;
  onHide(): void;

  // Enabled Tabs
  isGlobalEnabled?: boolean;
  isProjectEnabled?: boolean;
  isRevisionEnabled?: boolean;
};

export const TabIcons = {
  [Tab.Revision]: icons.codeCommit,
  [Tab.Project]: icons.folderBookmark,
  [Tab.Global]: icons.globeOutline,
} as const;

const ModalManagePackageLabels: VoidFunctionComponent<ModalProps> = ({
  onHide,
  show,
  context: { revision, title },
  parentRevisionLocator,
  isGlobalEnabled,
  isProjectEnabled,
  isRevisionEnabled,
}) => {
  // Detect bulk mode - no revision means bulk
  const isBulkMode = !revision;

  const packageId = Locator.parsePackageString(revision || '');
  const parentProjectLocator = Locator.parsePackageString(parentRevisionLocator || '');

  // Initialize state hooks for both modes
  const singlePackageState = usePackageLabelState(packageId, parentProjectLocator);
  const bulkState = useBulkPackageLabelState(parentProjectLocator);

  // Select the appropriate state based on mode
  const state = isBulkMode ? bulkState : singlePackageState;

  if (
    !show ||
    (isRevisionEnabled && !parentRevisionLocator) ||
    (!isGlobalEnabled && !isProjectEnabled && !isRevisionEnabled)
  ) {
    return null;
  }

  const onSave = async () => {
    // Check if bulk mode has dependencies selected
    if (isBulkMode && bulkState.selectedDependencies.length === 0) {
      return null;
    }

    switch (state.tabs.activeTab) {
      case Tab.Global:
        if (!isGlobalEnabled) {
          return null;
        }
        await state.onSaveGlobal();
        //  Close the modal
        onHide();
        break;
      case Tab.Project:
        if (!isProjectEnabled) {
          return null;
        }
        await state.onSaveProject(parentProjectLocator);
        //  Close the modal
        onHide();
        break;
      case Tab.Revision:
        if (!isRevisionEnabled || !parentRevisionLocator) {
          return null;
        }
        await state.onSaveRevision(parentRevisionLocator);
        //  Close the modal
        onHide();
        break;
      default:
        return null;
    }
    return null;
  };

  return (
    <Modal
      onHide={onHide}
      animation
      show={show}
      backdrop="static"
      keyboard={false}
      className={styles.modalManagePackageLabels}
    >
      <Modal.Header closeButton>
        <Modal.Title>Manage Labels</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isBulkMode ? (
          <ManagePackageLabelsBulkContent
            parentRevisionLocator={parentRevisionLocator || ''}
            state={bulkState}
            isGlobalEnabled={isGlobalEnabled}
            isProjectEnabled={isProjectEnabled}
            isRevisionEnabled={isRevisionEnabled}
          />
        ) : (
          <ManagePackageLabelsContent
            context={{ revision, title }}
            state={singlePackageState}
            parentRevisionLocator={parentRevisionLocator}
            isGlobalEnabled={isGlobalEnabled}
            isProjectEnabled={isProjectEnabled}
            isRevisionEnabled={isRevisionEnabled}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup>
          <Button type="secondary" size="medium" onClick={onHide}>
            Cancel
          </Button>
          <Button
            type="primary"
            size="medium"
            isDisabled={state.hasNoPermission || (isBulkMode && bulkState.selectedDependencies.length === 0)}
            onClick={onSave}
          >
            Save
          </Button>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

export default ModalManagePackageLabels;
